import { z } from 'zod';
import type { StartingSituation } from './economy';
import type { NeedKey } from './play';

// The player's house (Jerry, 9 October 2026): after choosing a character the player lands in their first home,
// which their starting situation decides. They arrange it however they like (free placement on the floor), buy
// furniture with game money through the ledger, and the furniture looks after their needs (home.service.ts and
// play.ts). Sizes are metres; the room is drawn in the same block style as the city.

export const HOME_TYPES = {
  flatshare_room: {
    label: 'Room in a shared house',
    area: 'Stratford',
    description: 'Your own room, a shared kitchen and a bathroom rota nobody keeps to.',
    floor: { w: 4, d: 3.6 },
    rentPenceWeekly: 15_000,
    wallColour: '#efe4cf',
    floorColour: '#b98a5b',
  },
  studio: {
    label: 'Studio flat',
    area: 'Canning Town',
    description: 'One room, your own front door, and a kitchen in the corner.',
    floor: { w: 5.6, d: 4.4 },
    rentPenceWeekly: 28_000,
    wallColour: '#e8edef',
    floorColour: '#8c7a6b',
  },
  council_flat: {
    label: 'Council flat',
    area: 'Royal Docks',
    description: 'A proper flat on the estate: a living room, a kitchen and a view of the docks.',
    floor: { w: 6.4, d: 5 },
    rentPenceWeekly: 12_000,
    wallColour: '#f1ecdf',
    floorColour: '#a3a09a',
  },
} as const;
export type HomeType = keyof typeof HOME_TYPES;
export const HOME_TYPE_KEYS = Object.keys(HOME_TYPES) as HomeType[];
export const HOME_BY_SITUATION: Record<StartingSituation, HomeType> = {
  student: 'flatshare_room',
  young_worker: 'studio',
  new_arrival: 'council_flat',
};

// ---- the furniture catalogue: what each thing is, costs, and does for a need (a better tier does more)

export const FURNITURE_CATEGORIES = {
  sleep: 'Sleep',
  bathroom: 'Bathroom',
  kitchen: 'Kitchen',
  living: 'Living room',
  decor: 'Decoration',
} as const;
export type FurnitureCategory = keyof typeof FURNITURE_CATEGORIES;
// how the room draws it (packages/game house/room.ts)
export type FurnitureShape =
  | 'bed'
  | 'wardrobe'
  | 'shower'
  | 'toilet'
  | 'sink'
  | 'cooker'
  | 'fridge'
  | 'table'
  | 'chair'
  | 'desk'
  | 'sofa'
  | 'armchair'
  | 'tv'
  | 'shelf'
  | 'rug'
  | 'plant'
  | 'lamp'
  | 'poster';
export type FurnitureItem = {
  id: string;
  label: string;
  icon: string;
  category: FurnitureCategory;
  shape: FurnitureShape;
  pricePence: number;
  // footprint and height in metres, before rotation
  size: { w: number; d: number; h: number };
  colour: string;
  // 1 basic, 2 good, 3 best
  tier: 1 | 2 | 3;
  // what using it does for the character
  need?: NeedKey;
  restores?: number;
  // posters and rugs sit against or on the floor and may share their space with other things
  flat?: boolean;
};
const f = (
  id: string,
  label: string,
  icon: string,
  category: FurnitureCategory,
  shape: FurnitureShape,
  pricePence: number,
  size: [number, number, number],
  colour: string,
  tier: 1 | 2 | 3,
  effect?: { need: NeedKey; restores: number },
  flat?: boolean,
): FurnitureItem => ({
  id,
  label,
  icon,
  category,
  shape,
  pricePence,
  size: { w: size[0], d: size[1], h: size[2] },
  colour,
  tier,
  ...(effect ?? {}),
  ...(flat ? { flat } : {}),
});
export const FURNITURE: FurnitureItem[] = [
  // sleep
  f('bed_single', 'Single bed', '🛏️', 'sleep', 'bed', 8_900, [1, 2, 0.5], '#5b7fb5', 1, {
    need: 'energy',
    restores: 55,
  }),
  f('bed_double', 'Double bed', '🛏️', 'sleep', 'bed', 34_900, [1.5, 2, 0.55], '#7a5bb5', 2, {
    need: 'energy',
    restores: 70,
  }),
  f('bed_king', 'King-size bed', '🛏️', 'sleep', 'bed', 119_900, [1.9, 2.1, 0.6], '#b55b7a', 3, {
    need: 'energy',
    restores: 85,
  }),
  f('wardrobe', 'Wardrobe', '🚪', 'sleep', 'wardrobe', 18_000, [1.2, 0.6, 2], '#8b6a4a', 1),
  // bathroom
  f('toilet', 'Toilet', '🚽', 'bathroom', 'toilet', 8_000, [0.5, 0.7, 0.8], '#f4f4f0', 1, {
    need: 'bladder',
    restores: 100,
  }),
  f('shower_basic', 'Shower', '🚿', 'bathroom', 'shower', 12_000, [0.9, 0.9, 2.1], '#cfe3ea', 1, {
    need: 'hygiene',
    restores: 50,
  }),
  f('shower_rain', 'Rainfall shower', '🚿', 'bathroom', 'shower', 45_000, [1, 1, 2.1], '#9fcbd9', 2, {
    need: 'hygiene',
    restores: 70,
  }),
  f('bath', 'Bath', '🛁', 'bathroom', 'shower', 95_000, [0.8, 1.7, 0.6], '#eef7fa', 3, {
    need: 'hygiene',
    restores: 85,
  }),
  f('sink', 'Basin', '🧼', 'bathroom', 'sink', 6_000, [0.5, 0.4, 0.85], '#f4f4f0', 1),
  // kitchen
  f('cooker_hob', 'Cooker', '🍳', 'kitchen', 'cooker', 15_000, [0.6, 0.6, 0.9], '#d9d9d9', 1, {
    need: 'hunger',
    restores: 45,
  }),
  f('cooker_range', 'Range cooker', '🍳', 'kitchen', 'cooker', 60_000, [0.9, 0.6, 0.9], '#3a3f47', 2, {
    need: 'hunger',
    restores: 60,
  }),
  f('fridge', 'Fridge', '🧊', 'kitchen', 'fridge', 22_000, [0.6, 0.6, 1.7], '#e6ebee', 1),
  f('table', 'Table', '🍽️', 'kitchen', 'table', 9_000, [1.2, 0.8, 0.75], '#c89b6c', 1),
  f('chair', 'Chair', '🪑', 'kitchen', 'chair', 3_500, [0.45, 0.45, 0.9], '#a8744a', 1),
  // living
  f('sofa', 'Sofa', '🛋️', 'living', 'sofa', 25_000, [1.8, 0.9, 0.8], '#4f7a63', 1, { need: 'fun', restores: 6 }),
  f('armchair', 'Armchair', '💺', 'living', 'armchair', 12_000, [0.9, 0.9, 0.8], '#b5713b', 1),
  f('tv_small', 'Small telly', '📺', 'living', 'tv', 15_000, [0.9, 0.3, 0.6], '#1f2429', 1, {
    need: 'fun',
    restores: 12,
  }),
  f('tv_big', 'Big telly', '📺', 'living', 'tv', 70_000, [1.5, 0.3, 0.9], '#14181c', 2, { need: 'fun', restores: 22 }),
  f('desk', 'Desk', '🖥️', 'living', 'desk', 13_000, [1.2, 0.6, 0.75], '#d5b48c', 1),
  f('shelf', 'Bookshelf', '📚', 'living', 'shelf', 11_000, [0.8, 0.3, 1.8], '#7d5a3c', 1),
  // decor
  f('rug', 'Rug', '🟫', 'decor', 'rug', 6_000, [1.6, 1.1, 0.02], '#c2553f', 1, undefined, true),
  f('plant', 'Plant', '🪴', 'decor', 'plant', 2_500, [0.4, 0.4, 1], '#4b8a3e', 1),
  f('lamp', 'Lamp', '💡', 'decor', 'lamp', 4_000, [0.35, 0.35, 1.5], '#e9c46a', 1),
  f('poster', 'Poster', '🖼️', 'decor', 'poster', 1_500, [0.6, 0.05, 0.8], '#e76f51', 1, undefined, true),
];
export const FURNITURE_BY_ID = new Map(FURNITURE.map((item) => [item.id, item]));
// selling back: half the price; the things the home came with were free and sell for nothing
export const SELL_BACK_SHARE = 0.5;
export const MAX_HOME_ITEMS = 60;

// what every home starts with
type Placed = { item: string; x: number; z: number; rotation: 0 | 1 | 2 | 3 };
export const STARTER_FURNITURE: Record<HomeType, Placed[]> = {
  flatshare_room: [
    { item: 'bed_single', x: -1.3, z: -0.5, rotation: 0 },
    { item: 'desk', x: 1.2, z: -1.4, rotation: 0 },
    { item: 'chair', x: 1.2, z: -0.8, rotation: 2 },
    { item: 'wardrobe', x: 1.3, z: 1.2, rotation: 1 },
    { item: 'toilet', x: -1.6, z: 1.3, rotation: 1 },
    { item: 'shower_basic', x: -0.6, z: 1.3, rotation: 0 },
    { item: 'cooker_hob', x: 0.4, z: 1.3, rotation: 0 },
  ],
  studio: [
    { item: 'bed_double', x: -1.8, z: -0.9, rotation: 0 },
    { item: 'wardrobe', x: -2.2, z: 1.5, rotation: 0 },
    { item: 'sofa', x: 1.2, z: 0.2, rotation: 0 },
    { item: 'tv_small', x: 1.2, z: -1.8, rotation: 0 },
    { item: 'table', x: 1.6, z: 1.6, rotation: 0 },
    { item: 'chair', x: 1.6, z: 0.9, rotation: 2 },
    { item: 'cooker_hob', x: -0.4, z: 1.9, rotation: 0 },
    { item: 'fridge', x: 0.3, z: 1.9, rotation: 0 },
    { item: 'toilet', x: 2.4, z: -1.9, rotation: 1 },
    { item: 'shower_basic', x: -0.6, z: -1.75, rotation: 0 },
  ],
  council_flat: [
    { item: 'bed_double', x: -2.3, z: -1.3, rotation: 0 },
    { item: 'wardrobe', x: -2.6, z: 1.6, rotation: 0 },
    { item: 'sofa', x: 1.4, z: 0.4, rotation: 0 },
    { item: 'armchair', x: 2.6, z: -0.8, rotation: 3 },
    { item: 'tv_small', x: 1.4, z: -2.1, rotation: 0 },
    { item: 'table', x: 1.8, z: 1.9, rotation: 0 },
    { item: 'chair', x: 1.4, z: 1.2, rotation: 2 },
    { item: 'chair', x: 2.2, z: 1.2, rotation: 2 },
    { item: 'cooker_hob', x: -0.6, z: 2.2, rotation: 0 },
    { item: 'fridge', x: 0.1, z: 2.2, rotation: 0 },
    { item: 'toilet', x: 2.8, z: -2.2, rotation: 1 },
    { item: 'shower_basic', x: -0.8, z: -2.05, rotation: 0 },
    { item: 'plant', x: 2.9, z: 2.2, rotation: 0 },
  ],
};

// ---- placement rules (checked by the app as the player drags, and by the API before anything is saved)

export const Rotation = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export type Rotation = z.infer<typeof Rotation>;
// a thing's footprint on the floor once turned
export const footprint = (item: FurnitureItem, rotation: Rotation) =>
  rotation % 2 === 0 ? { w: item.size.w, d: item.size.d } : { w: item.size.d, d: item.size.w };
export type PlacedThing = { id: string; item: string; x: number; z: number; rotation: Rotation };
// why a layout is refused, or nothing when it fits
export function placementProblem(floor: { w: number; d: number }, placed: PlacedThing[]): string | null {
  const { w, d } = floor;
  const boxes: { id: string; flat: boolean; left: number; right: number; top: number; bottom: number }[] = [];
  for (const thing of placed) {
    const item = FURNITURE_BY_ID.get(thing.item);
    if (!item) return 'That is not a thing you can place.';
    const foot = footprint(item, thing.rotation);
    const box = {
      id: thing.id,
      flat: Boolean(item.flat),
      left: thing.x - foot.w / 2,
      right: thing.x + foot.w / 2,
      top: thing.z - foot.d / 2,
      bottom: thing.z + foot.d / 2,
    };
    const slack = 0.01;
    if (
      box.left < -w / 2 - slack ||
      box.right > w / 2 + slack ||
      box.top < -d / 2 - slack ||
      box.bottom > d / 2 + slack
    )
      return `${item.label} does not fit inside the room there.`;
    for (const other of boxes) {
      if (box.flat || other.flat) continue;
      if (
        box.left < other.right - slack &&
        box.right > other.left + slack &&
        box.top < other.bottom - slack &&
        box.bottom > other.top + slack
      )
        return `${item.label} overlaps something else.`;
    }
    boxes.push(box);
  }
  return null;
}

// ---- rent (Jerry, 10 October 2026): due every Monday (UK time), paid by hand at home; unpaid weeks become
// arrears, and after three missed weeks in a row the player moves to the cheapest home with their things put away

export const RENT_WEEKDAY = 1; // Monday
export const EVICTION_AFTER_MISSED_WEEKS = 3;
export const CHEAPEST_HOME: HomeType = 'council_flat';
export const londonParts = (at: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    weekday: 'short',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    second: Number(get('second')),
    weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday')),
  };
};
// "Sat 24 Oct, 9:00 pm": a moment as London's clock shows it (for messages the server writes)
export const londonMoment = (at: Date | string) =>
  new Date(at).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Europe/London',
  });
// the game's day follows London's clock: night from 7 pm to 7 am (the places go dark, lamps on)
export const isLondonNight = (at: Date = new Date()) => {
  const hour = londonParts(at).hour;
  return hour < 7 || hour >= 19;
};
// midnight at the start of a London calendar day, as an instant
const londonMidnight = (year: number, month: number, day: number) => {
  const guess = Date.UTC(year, month - 1, day, 0, 0, 0);
  const seen = londonParts(new Date(guess));
  // the guess is UTC midnight; shift by however far London's clock is from it (0 or 1 hour)
  const offsetMinutes = (seen.day - day) * 1440 + seen.hour * 60 + seen.minute;
  return new Date(guess - offsetMinutes * 60_000);
};
// the start of today in London, and the next midnight (when a daily limit starts again)
export function londonDayStart(at: Date = new Date()): Date {
  const now = londonParts(at);
  return londonMidnight(now.year, now.month, now.day);
}
export function nextLondonMidnight(at: Date = new Date()): Date {
  const now = londonParts(at);
  const tomorrow = londonParts(new Date(Date.UTC(now.year, now.month - 1, now.day + 1, 12)));
  return londonMidnight(tomorrow.year, tomorrow.month, tomorrow.day);
}
// an hour of a London calendar day as an instant (6 pm is 6 pm on the clock, summer or winter)
export function londonAt(year: number, month: number, day: number, hour: number): Date {
  const guess = new Date(londonMidnight(year, month, day).getTime() + hour * 3_600_000);
  // a clock change earlier that day: move back onto the hour the clock shows
  return new Date(guess.getTime() - (londonParts(guess).hour - hour) * 3_600_000);
}
// every London day's `hour` o'clock after `from`, up to and including `to` (oldest first), with the London day
// and its weekday (Monday 0 … Sunday 6)
export function londonHoursBetween(from: Date, to: Date, hour: number) {
  const out: { at: Date; day: string; weekday: number }[] = [];
  const start = londonParts(from);
  for (let offset = 0; offset < 400; offset++) {
    const noon = londonParts(new Date(Date.UTC(start.year, start.month - 1, start.day + offset, 12)));
    const at = londonAt(noon.year, noon.month, noon.day, hour);
    if (at > to) break;
    if (at > from)
      out.push({
        at,
        day: `${noon.year}-${String(noon.month).padStart(2, '0')}-${String(noon.day).padStart(2, '0')}`,
        weekday: (noon.weekday + 6) % 7,
      });
  }
  return out;
}

// the Monday after `from` (strictly later), at 00:00 London time
export function nextRentDue(from: Date): Date {
  const now = londonParts(from);
  const daysAhead = (RENT_WEEKDAY - now.weekday + 7) % 7 || 7;
  const target = new Date(Date.UTC(now.year, now.month - 1, now.day + daysAhead, 12));
  const t = londonParts(target);
  return londonMidnight(t.year, t.month, t.day);
}
export const RentView = z.object({
  weeklyPence: z.number().int(),
  owedPence: z.number().int(),
  // when the next week's rent is added
  dueAt: z.string(),
  missedWeeks: z.number().int(),
});
export type RentView = z.infer<typeof RentView>;

// ---- the API

export const OwnedThing = z.object({
  id: z.string().uuid(),
  item: z.string(),
  starter: z.boolean(),
  pricePence: z.number().int(),
  placed: z.object({ x: z.number(), z: z.number(), rotation: Rotation }).nullable(),
});
export type OwnedThing = z.infer<typeof OwnedThing>;
// the room as it is drawn: its floor (after any extensions) and its colours
export const RoomShape = z.object({
  floor: z.object({ w: z.number(), d: z.number() }),
  wallColour: z.string(),
  floorColour: z.string(),
});
export type RoomShape = z.infer<typeof RoomShape>;
export const HomeView = z.object({
  // the kind of home (Content → Houses), what it is called and where
  type: z.string(),
  label: z.string(),
  area: z.string(),
  description: z.string(),
  icon: z.string(),
  room: RoomShape,
  // rent while it is rented (nothing once it is owned)
  rentPenceWeekly: z.number().int(),
  rent: RentView,
  things: z.array(OwnedThing),
  // owned: what was paid (with extensions), what selling it back pays, how far it is extended and the crew's
  // work under way
  owned: z.boolean(),
  paidPence: z.number().int(),
  sellPence: z.number().int(),
  extended: z.number().int(),
  extensions: z.number().int(),
  extendingUntil: z.string().nullable(),
  nextExtension: z
    .object({ pricePence: z.number().int(), hours: z.number(), floor: z.object({ w: z.number(), d: z.number() }) })
    .nullable(),
  buyPence: z.number().int(),
});
export type HomeView = z.infer<typeof HomeView>;
export const MyHomeReply = z.object({ home: HomeView, walletPence: z.number().int() });
export type MyHomeReply = z.infer<typeof MyHomeReply>;

const coordinate = z.number().finite().min(-10).max(10);
// the whole arrangement at once: every owned thing, where it stands or put away
export const HomeLayout = z
  .object({
    placed: z
      .array(z.object({ id: z.string().uuid(), x: coordinate, z: coordinate, rotation: Rotation }).strict())
      .max(MAX_HOME_ITEMS),
  })
  .strict();
export type HomeLayout = z.infer<typeof HomeLayout>;
export const HomeBuy = z.object({ item: z.string().min(1).max(40) }).strict();
export type HomeBuy = z.infer<typeof HomeBuy>;
export const HomeSell = z.object({ id: z.string().uuid() }).strict();
export type HomeSell = z.infer<typeof HomeSell>;
export const HomeActionReply = z.object({ home: HomeView, walletPence: z.number().int(), message: z.string() });
export type HomeActionReply = z.infer<typeof HomeActionReply>;

// ---- using the home: each thing you do at home needs a piece of furniture, and a better piece does more

// the shape the activity uses, and a second shape that adds to it when it stands in the room too
export const HOME_ACTIVITIES: Record<string, { need: NeedKey; uses: FurnitureShape; with?: FurnitureShape }> = {
  sleep: { need: 'energy', uses: 'bed' },
  home_shower: { need: 'hygiene', uses: 'shower' },
  cook: { need: 'hunger', uses: 'cooker' },
  watch_tv: { need: 'fun', uses: 'tv', with: 'sofa' },
  home_loo: { need: 'bladder', uses: 'toilet' },
};
export const SHAPE_NAMES: Partial<Record<FurnitureShape, string>> = {
  bed: 'a bed',
  shower: 'a shower or a bath',
  cooker: 'a cooker',
  tv: 'a telly',
  toilet: 'a toilet',
};
export type HomeUse =
  { ok: true; restores: number; using: FurnitureItem; also: FurnitureItem | null } | { ok: false; missing: string };
// what the activity does with the furniture standing in the room (null when it is not a home activity)
export function homeUse(activityId: string, placedItems: FurnitureItem[]): HomeUse | null {
  const rule = HOME_ACTIVITIES[activityId];
  if (!rule) return null;
  const best = (shape: FurnitureShape) =>
    placedItems
      .filter((item) => item.shape === shape && item.need === rule.need && item.restores)
      .sort((a, b) => b.restores! - a.restores!)[0] ?? null;
  const using = best(rule.uses);
  if (!using) return { ok: false, missing: SHAPE_NAMES[rule.uses] ?? rule.uses };
  const also = rule.with ? best(rule.with) : null;
  return { ok: true, restores: using.restores! + (also?.restores ?? 0), using, also };
}
